import React, { useState, useEffect, useCallback } from 'react';
import { Table, Form, Input, Select, Button, Tag, Space, DatePicker, message } from 'antd';
import { ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, ExportOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import DraggableModal from '../../components/DraggableModal';
import api from '../../api';
import { getStoredUser, isSuperAdmin } from '../../userAuth';

const typeMap = { 1: '借用申请', 2: '变更申请', 3: '处置申请', 4: '处置撤回申请' };
const typeColor = { 1: 'blue', 2: 'green', 3: 'red', 4: 'orange' };
const statusMap = { 0: '待审核', 1: '已通过', 2: '已驳回' };
const statusColor = { 0: 'orange', 1: 'green', 2: 'red' };
const DEFAULT_DATE_RANGE = [dayjs().startOf('month'), dayjs().endOf('month')];

function formatDateTime(t) {
  if (!t) return '-';
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return String(t);
  return d.toLocaleString('zh-CN');
}

function renderApplyInfoBlock(record) {
  const assets = Array.isArray(record.assets) ? record.assets : [];
  if (!assets.length) {
    return <span style={{ color: '#999' }}>暂无明细</span>;
  }
  const t = record.apply_type;
  if (t === 1) {
    return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {assets.map((a, i) => (
          <div
            key={a.asset_id || i}
            style={{
              padding: '8px 10px',
              background: '#f7f9fc',
              borderRadius: 6,
              fontSize: 13,
              lineHeight: 1.65,
            }}
          >
            <div><b>借用资产：</b>{a.asset_name || '-'}</div>
            <div><b>存放地点：</b>{a.old_location_name || '-'}</div>
            <div><b>原使用人：</b>{a.old_user_name || '-'}</div>
          </div>
        ))}
      </div>
    );
  }
  if (t === 2) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {assets.map((a, i) => (
          <div
            key={a.asset_id || i}
            style={{
              padding: '8px 10px',
              background: '#f7f9fc',
              borderRadius: 6,
              fontSize: 13,
              lineHeight: 1.65,
            }}
          >
            <div><b>资产名称：</b>{a.asset_name || '-'}</div>
            <div><b>原使用人：</b>{a.old_user_name || '-'}</div>
            <div><b>原存放地点：</b>{a.old_location_name || '-'}</div>
            <div><b>现使用人：</b>{a.new_user_name || '-'}</div>
            <div><b>现存放地点：</b>{a.new_location_name || '-'}</div>
          </div>
        ))}
      </div>
    );
  }
  if (t === 3) {
    return (
      <div style={{ fontSize: 13, lineHeight: 1.65 }}>
        <b>{t === 4 ? '撤回资产：' : '处置资产：'}</b>
        {assets.map(a => a.asset_name).filter(Boolean).join('、') || '-'}
      </div>
    );
  }
  return null;
}

export default function Approvals() {
  const user = getStoredUser();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [keyword, setKeyword] = useState('');
  const [typeFilter, setTypeFilter] = useState(undefined);
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [dateRange, setDateRange] = useState(DEFAULT_DATE_RANGE);
  const [detailModal, setDetailModal] = useState(false);
  const [approveModal, setApproveModal] = useState(false);
  const [current, setCurrent] = useState(null);
  const [remark, setRemark] = useState('');
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });

  const fetchData = useCallback(async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (keyword) params.keyword = keyword;
      if (typeFilter !== undefined) params.apply_type = typeFilter;
      if (statusFilter !== undefined) params.status = statusFilter;
      if (dateRange?.[0]) params.start_date = dateRange[0].format('YYYY-MM-DD');
      if (dateRange?.[1]) params.end_date = dateRange[1].format('YYYY-MM-DD');
      const res = await api.get('/applies/all', { params });
      const items = res?.items || [];
      setData(items);
      setPagination(p => ({ ...p, current: page, pageSize, total: res?.total || 0 }));
    } catch {
      message.error('获取审批列表失败');
    }
    setLoading(false);
  }, [keyword, typeFilter, statusFilter, dateRange]);

  const fetchStats = useCallback(async () => {
    try {
      // Fetch all to compute stats
      const [pendRes, appRes, rejRes] = await Promise.all([
        api.get('/applies/all', {
          params: {
            status: 0,
            page: 1,
            page_size: 1,
            start_date: dateRange?.[0]?.format('YYYY-MM-DD'),
            end_date: dateRange?.[1]?.format('YYYY-MM-DD'),
          },
        }),
        api.get('/applies/all', {
          params: {
            status: 1,
            page: 1,
            page_size: 1,
            start_date: dateRange?.[0]?.format('YYYY-MM-DD'),
            end_date: dateRange?.[1]?.format('YYYY-MM-DD'),
          },
        }),
        api.get('/applies/all', {
          params: {
            status: 2,
            page: 1,
            page_size: 1,
            start_date: dateRange?.[0]?.format('YYYY-MM-DD'),
            end_date: dateRange?.[1]?.format('YYYY-MM-DD'),
          },
        }),
      ]);
      setStats({
        pending: pendRes?.total || 0,
        approved: appRes?.total || 0,
        rejected: rejRes?.total || 0,
      });
    } catch { /* ignore */ }
  }, [dateRange]);

  useEffect(() => { fetchData(); fetchStats(); }, [fetchData, fetchStats]);

  if (!isSuperAdmin(user)) return <div style={{ padding: 40, textAlign: 'center', fontSize: 18 }}>无权限</div>;

  const handleSearch = () => fetchData(1, pagination.pageSize);
  const handleReset = () => {
    setKeyword('');
    setTypeFilter(undefined);
    setStatusFilter(undefined);
    setDateRange(DEFAULT_DATE_RANGE);
  };

  const handleApprove = async (action) => {
    try {
      await api.put(`/applies/${current.id}/approve`, { action, remark });
      message.success(action === 'approve' ? '已通过' : '已驳回');
      setApproveModal(false);
      setRemark('');
      fetchData(pagination.current, pagination.pageSize);
      fetchStats();
    } catch {
      message.error('操作失败');
    }
  };

  const openDetail = (record) => { setCurrent(record); setDetailModal(true); };
  const openApprove = (record) => { setCurrent(record); setRemark(''); setApproveModal(true); };

  const handleExport = () => {
    message.info('导出功能开发中');
  };

  const columns = [
    {
      title: '申请类型',
      dataIndex: 'apply_type',
      key: 'apply_type',
      render: (t) => <Tag color={typeColor[t]}>{typeMap[t] || '未知'}</Tag>,
    },
    {
      title: '申请信息',
      key: 'info',
      render: (_, record) => {
        const username = record.apply_user?.username || '未知';
        const typeText = typeMap[record.apply_type] || '申请';
        const assetNames = record.asset_names
          || (Array.isArray(record.assets)
            ? record.assets.map((x) => x.asset_name).filter(Boolean).join('、')
            : '');
        return `${username} 申请${typeText}${assetNames ? ` ${assetNames}` : ''}`;
      },
    },
    {
      title: '申请人',
      key: 'apply_user',
      render: (_, record) => {
        const u = record.apply_user;
        return u ? `${u.username}（${u.work_no}）` : '-';
      },
    },
    {
      title: '申请时间',
      dataIndex: 'create_time',
      key: 'create_time',
      render: (t) => t ? new Date(t).toLocaleString('zh-CN') : '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (s) => <Tag color={statusColor[s]}>{statusMap[s] || '未知'}</Tag>,
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button type="link" style={{ padding: 0 }} onClick={() => openDetail(record)}>
            详情
          </Button>
          {record.status === 0 ? (
            <Button type="link" style={{ padding: 0 }} onClick={() => openApprove(record)}>
              审批
            </Button>
          ) : (
            <Button type="link" style={{ padding: 0 }} onClick={() => openApprove(record)}>
              干预
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const statCards = [
    { label: '待审批', value: stats.pending, color: '#fa8c16', bg: '#fff7e6', icon: <ClockCircleOutlined style={{ fontSize: 20, color: '#fa8c16' }} /> },
    { label: '已通过', value: stats.approved, color: '#52c41a', bg: '#f6ffed', icon: <CheckCircleOutlined style={{ fontSize: 20, color: '#52c41a' }} /> },
    { label: '已驳回', value: stats.rejected, color: '#ff4d4f', bg: '#fff2f0', icon: <CloseCircleOutlined style={{ fontSize: 20, color: '#ff4d4f' }} /> },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>审批管理</h1>
        <Button icon={<ExportOutlined />} onClick={handleExport}>导出审批记录</Button>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        {statCards.map((card, idx) => (
          <div key={idx} style={{ background: '#fff', borderRadius: 8, padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 14, color: '#666' }}>{card.label}</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: card.color, marginTop: 8 }}>
                  {card.value}
                </div>
              </div>
              <div style={{ width: 40, height: 40, borderRadius: 20, background: card.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {card.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Search Bar */}
      <div style={{ background: '#fff', borderRadius: 8, padding: 16, marginBottom: 24 }}>
        <Space wrap>
          <Input
            placeholder="搜索申请人/资产名称"
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            style={{ width: 220 }}
            allowClear
          />
          <Select
            placeholder="全部类型"
            value={typeFilter}
            onChange={v => setTypeFilter(v)}
            allowClear
            style={{ width: 140 }}
          >
            <Select.Option value={1}>借用申请</Select.Option>
            <Select.Option value={2}>变更申请</Select.Option>
            <Select.Option value={3}>处置申请</Select.Option>
            <Select.Option value={4}>处置撤回申请</Select.Option>
          </Select>
          <Select
            placeholder="全部状态"
            value={statusFilter}
            onChange={v => setStatusFilter(v)}
            allowClear
            style={{ width: 140 }}
          >
            <Select.Option value={0}>待审批</Select.Option>
            <Select.Option value={1}>已通过</Select.Option>
            <Select.Option value={2}>已驳回</Select.Option>
          </Select>
          <DatePicker.RangePicker
            value={dateRange}
            onChange={(values) => setDateRange(values || DEFAULT_DATE_RANGE)}
            allowClear={false}
          />
          <Button type="primary" onClick={handleSearch}>搜索</Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>
      </div>

      {/* Stats Text */}
      <div style={{ marginBottom: 12, color: '#666', fontSize: 14 }}>
        共 {pagination.total} 条审批记录
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 8 }}>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => fetchData(page, pageSize),
          }}
        />
      </div>

      {/* Detail Modal */}
      <DraggableModal
        title="申请详情"
        open={detailModal}
        width={560}
        onCancel={() => setDetailModal(false)}
        footer={<Button onClick={() => setDetailModal(false)}>关闭</Button>}
      >
        {current && (
          <div style={{ lineHeight: 1.8 }}>
            <p><b>申请类型：</b><Tag color={typeColor[current.apply_type]}>{typeMap[current.apply_type]}</Tag></p>
            <p><b>申请人：</b>{current.apply_user?.username}（{current.apply_user?.work_no}）</p>
            <div style={{ marginBottom: 12 }}>
              <b style={{ display: 'block', marginBottom: 6 }}>申请信息：</b>
              {renderApplyInfoBlock(current)}
            </div>
            <p><b>申请时间：</b>{formatDateTime(current.create_time)}</p>
            <p>
              <b>审批人：</b>
              {current.approver
                ? `${current.approver.username}（${current.approver.work_no || '-'}）`
                : '-'}
            </p>
            <p>
              <b>审核状态：</b>
              <Tag color={statusColor[current.status]}>
                {current.status_text || statusMap[current.status] || '未知'}
              </Tag>
            </p>
            <p><b>审核时间：</b>{formatDateTime(current.approve_time)}</p>
            <p><b>备注：</b>{current.remark != null && current.remark !== '' ? current.remark : '-'}</p>
          </div>
        )}
      </DraggableModal>

      {/* Approve Modal */}
      <DraggableModal
        title={current?.status === 0 ? '审批' : '干预审批'}
        open={approveModal}
        width={560}
        onCancel={() => setApproveModal(false)}
        footer={
          <Space>
            <Button onClick={() => setApproveModal(false)}>取消</Button>
            <Button danger onClick={() => handleApprove('reject')}>驳回</Button>
            <Button type="primary" onClick={() => handleApprove('approve')}>通过</Button>
          </Space>
        }
      >
        {current && (
          <div style={{ marginBottom: 16, lineHeight: 1.8 }}>
            <p><b>申请类型：</b><Tag color={typeColor[current.apply_type]}>{typeMap[current.apply_type]}</Tag></p>
            <p><b>申请人：</b>{current.apply_user?.username}（{current.apply_user?.work_no}）</p>
            <div style={{ marginBottom: 12 }}>
              <b style={{ display: 'block', marginBottom: 6 }}>申请信息：</b>
              {renderApplyInfoBlock(current)}
            </div>
            <p><b>申请时间：</b>{formatDateTime(current.create_time)}</p>
            <p>
              <b>审批人：</b>
              {current.approver
                ? `${current.approver.username}（${current.approver.work_no || '-'}）`
                : '-'}
            </p>
            <p>
              <b>审核状态：</b>
              <Tag color={statusColor[current.status]}>
                {current.status_text || statusMap[current.status] || '未知'}
              </Tag>
            </p>
            <p><b>审核时间：</b>{formatDateTime(current.approve_time)}</p>
            <p><b>备注：</b>{current.remark != null && current.remark !== '' ? current.remark : '-'}</p>
          </div>
        )}
        <Form.Item label="审批备注">
          <Input.TextArea
            rows={3}
            value={remark}
            onChange={e => setRemark(e.target.value)}
            placeholder="请输入审批备注（可选）"
          />
        </Form.Item>
      </DraggableModal>
    </div>
  );
}
